import { StyledFlex } from "@lib/components/MiniComponents/Table/index.styled";
import { UseMutationProps } from "@lib/types/Api";
import { Form, Input, Modal, Select, Space, Switch } from "antd";
import { useCallback, useEffect, useMemo } from "react";
import { useIntl } from "react-intl";
import { customerLeadSchema as yupSchema } from "@lib/schemas/customer";
import { createYupSync } from "@lib/helpers/TableHelper";
import { convertNumericFields } from "@lib/types/General";
import { FormattedMessage } from "react-intl";
import { ActionButton } from "@lib/components/MiniComponents/Buttons";
import { CustomerLead } from "@lib/types/CustomerLead";
import { useQuery } from "@tanstack/react-query";
import { apiGetCustomers } from "@lib/api/customer";
import CustomerName from "@lib/components/MiniComponents/Others/CustomerName";
import { useTypedSelector } from "@lib/redux";
import { AuthRoles } from "@lib/types";

const layout = {
  labelCol: { span: 6 },
  wrapperCol: { span: 18 },
};

type Props = {
  openModal: boolean;
  toggleModal: (status?: boolean) => void;
  mutationQuery: UseMutationProps<CustomerLead>;
  customerLead?: CustomerLead;
};

export default function CustomerLeadModal({
  openModal,
  toggleModal,
  mutationQuery,
  customerLead,
}: Props) {
  const { messages } = useIntl();
  const [form] = Form.useForm();
  const { mutate, isPending } = mutationQuery;
  const { user } = useTypedSelector((state) => state.auth);
  const userRole = user?.role;

  const initialValues = useMemo(() => {
    return customerLead
      ? convertNumericFields(customerLead)
      : ({
          isDisabled: false,
          firstName: "",
          lastName: "",
          baseUserId: "",
          customerId:
            userRole === AuthRoles.Admin ? "" : customerLead?.customerId,
        } as CustomerLead);
  }, [customerLead]);

  const handleResetForm = useCallback(() => {
    form?.setFieldsValue(initialValues);
  }, [initialValues]);

  const handleClose = useCallback(() => {
    handleResetForm();
    toggleModal(false);
  }, [toggleModal]);

  useEffect(() => {
    if (!openModal) return;

    handleResetForm();
  }, [openModal]);

  const { data: customers, isLoading: customersIsLoading } = useQuery({
    queryFn: () => apiGetCustomers({ limit: 100 }),
    queryKey: ["customers-dropdown"],
    enabled: !!openModal && userRole === AuthRoles.Admin,
  });

  return (
    <Modal
      title={
        <FormattedMessage
          id={customerLead ? "common.editTitle" : "common.createTitle"}
          values={{ title: messages["common.customerLead"] as string }}
        />
      }
      open={openModal}
      onCancel={handleClose}
      footer={null}
      confirmLoading={isPending}
      width={{
        xs: "90%",
        sm: "80%",
        md: "70%",
        lg: "60%",
        xl: "50%",
        xxl: "40%",
      }}
    >
      <Form<CustomerLead>
        {...layout}
        form={form}
        name={"customer-modal"}
        style={{ maxWidth: 800, marginTop: 20 }}
        autoComplete="off"
        initialValues={{ enabled: true, ...customerLead }}
        onFinish={(values) => mutate({ ...values, id: customerLead?.id })}
      >
        <Form.Item
          name="firstName"
          label={messages["common.firstName"] as string}
          rules={[createYupSync({ schema: yupSchema })]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="lastName"
          label={messages["common.lastName"] as string}
          rules={[createYupSync({ schema: yupSchema })]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="isDisabled"
          label={messages["common.status"] as string}
          rules={[createYupSync({ schema: yupSchema })]}
          valuePropName="isDisabled"
        >
          <Switch
            checked={initialValues?.isDisabled}
            checkedChildren={messages["common.enabled"] as string}
            unCheckedChildren={messages["common.disabled"] as string}
          />
        </Form.Item>

        {userRole === AuthRoles.Admin && (
          <Form.Item
            name="customerId"
            label={messages["common.customer"] as string}
            rules={[createYupSync({ schema: yupSchema })]}
          >
            <Select
              placeholder={messages["common.customer"] as string}
              allowClear
              showSearch
              loading={customersIsLoading}
              optionFilterProp="text"
              options={customers?.data?.data?.map((customer) => ({
                label: <CustomerName customer={customer} />,
                text: customer.name,
                value: customer.id,
              }))}
            />
          </Form.Item>
        )}

        {!customerLead && (
          <Form.Item
            name="baseUserId"
            label={messages["common.baseUserId"] as string}
            rules={[createYupSync({ schema: yupSchema })]}
          >
            <Input />
          </Form.Item>
        )}

        <StyledFlex style={{ justifyContent: "end" }}>
          <Space>
            {!customerLead && (
              <ActionButton
                type="reset"
                onClick={handleResetForm}
                loading={isPending}
              />
            )}

            <ActionButton
              type={!!customerLead ? "save" : "add"}
              loading={isPending}
            />
          </Space>
        </StyledFlex>
      </Form>
    </Modal>
  );
}
